import { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import useFeature from '~/client/hooks/feature/useFeature';
import useIsCloudFlareTurnstileEnabled from '~/client/hooks/feature/useIsCloudFlareTurnstileEnabled';
import useSite from '~/client/hooks/useSite';
import deserializeErrors from '~/shared/utils/errors/deserializeErrors';
import { useGetAuthClearanceMutation } from '../../Authenticate/AuthSteps/graphql/GetAuthClearanceMutation.generated';

const useAuthCaptchaClearance = () => {
  const [getAuthClearance] = useGetAuthClearanceMutation();
  const isRecaptchaV3Enabled = useFeature('isRecaptchaV3Enabled') ?? false;
  const isCloudFlareTurnstileEnabled = useIsCloudFlareTurnstileEnabled();

  const [isCaptchaClearedRequired, setIsCaptchaClearedRequired] =
    useState(false);
  const { site, isBridgeSite } = useSite();
  const isRecaptchaV3 = isRecaptchaV3Enabled && !isBridgeSite;
  const { executeRecaptcha } = useGoogleReCaptcha();

  const fetchCaptchaConfigRequiredClearance = useCallback(async () => {
    if (!isRecaptchaV3 || isCloudFlareTurnstileEnabled) {
      return;
    }
    const captchaToken = await executeRecaptcha?.();
    const { data } = await getAuthClearance({
      variables: {
        input: {
          site,
          captcha: captchaToken,
          captchaVersion: 'v3',
        },
      },
    });
    const authClearanceErrors = data?.userManagementGetAuthClearance?.errors;
    if (authClearanceErrors?.length) {
      const deserializedErrors = deserializeErrors(
        authClearanceErrors,
      ) as DeserializeError;
      if (deserializedErrors?.captcha) {
        setIsCaptchaClearedRequired(true);
      }
    }
  }, [
    getAuthClearance,
    setIsCaptchaClearedRequired,
    isCloudFlareTurnstileEnabled,
    site,
    isRecaptchaV3,
    executeRecaptcha,
  ]);

  useEffect(() => {
    if (executeRecaptcha) {
      fetchCaptchaConfigRequiredClearance();
    }
  }, [executeRecaptcha, fetchCaptchaConfigRequiredClearance]);

  return { isCaptchaClearedRequired };
};
export default useAuthCaptchaClearance;
